/* eslint-disable @typescript-eslint/no-magic-numbers */
import { clampBetween, SharedResources } from "@buildwithflux/shared";
import { isUndefined } from "lodash";
import { isNumeric } from "../../../helpers/isNumeric";
import { defaultFootprintStackupKey, defaultLayoutStackupKey, isCopperLayer, LayoutRules, stackups, } from "../../configs";
import { evaluateMechanicalValue } from "../../expression_engine/EvaluateExpression";
import { isExpression, normalizeEvaluatedExpression } from "../../expression_engine/ExpressionEvaluationHelpers";
import { SystemPcbLayoutRulesValues, } from "../../rules";
import { importantFlagString } from "../constants";
// A pity to have to tell prettier to ignore the whole class, but that's the only
// way to get it not to completely mess up the parseValue method overloads.
// prettier-ignore
export class RuleParser {
    constructor(rules, expressionEvaluationContext, nodeType) {
        var _a, _b, _c, _d;
        this.rules = rules;
        // Some rules are needed for parsing other rules, so we parse these separately first
        this.lengthUnit = RuleParser.parseValue("stringOption", (_b = (_a = rules.unit) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : LayoutRules.unit.default);
        this.angleUnit = RuleParser.parseValue("stringOption", (_d = (_c = rules.rotationUnit) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : LayoutRules.rotationUnit.default);
        this.nodeType = nodeType;
        this.expressionEvaluationContext = expressionEvaluationContext;
    }
    static stripImportantFlag(value) {
        const trimmed = value.trimEnd();
        if (trimmed.endsWith(importantFlagString)) {
            // If `trimmed` is `rectangle !important`, should return `rectangle`
            return trimmed.substring(0, trimmed.length - importantFlagString.length).trimEnd();
        }
        else {
            return value;
        }
    }
    static parseValue(valueType, value, options) {
        return this[valueType](this.stripImportantFlag(value.toString()), options);
    }
    static booleanValue(value, _options) {
        const normalizedValue = value.trim().toLowerCase();
        return normalizedValue === "true" || normalizedValue === "1";
    }
    static lengthValue(value, options) {
        if (value === SystemPcbLayoutRulesValues.auto) {
            return SystemPcbLayoutRulesValues.auto;
        }
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange);
        const clampValue = typeof evaluatedValue === 'number' ? evaluatedValue : options.default;
        // Note: NaN clampValue case covered by clampValueFields(..)
        return (typeof clampValue === 'number') ? clampValueFields(clampValue, options.minFieldValues, options.maxFieldValues) : undefined;
    }
    static angle(value, options) {
        if (value === SystemPcbLayoutRulesValues.auto) {
            return SystemPcbLayoutRulesValues.auto;
        }
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, options.angleUnit, "rad", options.anglePrecision, options.validRange);
        const clampValue = typeof evaluatedValue === 'number' ? evaluatedValue : options.default;
        // Note: NaN clampValue case covered by clampValueFields(..)
        return (typeof clampValue === 'number') ? clampValueFields(clampValue, options.minFieldValues, options.maxFieldValues) : undefined;
    }
    static cornerValues(value, options) {
        let result = {};
        if (!isExpression(value)) {
            const values = splitStringComponentsByWhitespace(value).map((v) => evaluateInputValue(v, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange));
            // implements https://www.w3schools.com/css/css3_borders.asp
            switch (values.length) {
                case 1:
                    result = { topLeft: Number(values[0]), topRight: Number(values[0]), bottomRight: Number(values[0]), bottomLeft: Number(values[0]) };
                    break;
                case 2:
                    result = { topLeft: Number(values[0]), bottomRight: Number(values[0]), topRight: Number(values[1]), bottomLeft: Number(values[1]) };
                    break;
                case 3:
                    result = { topLeft: Number(values[0]), topRight: Number(values[1]), bottomLeft: Number(values[1]), bottomRight: Number(values[2]) };
                    break;
                case 4:
                    result = { topLeft: Number(values[0]), topRight: Number(values[1]), bottomRight: Number(values[2]), bottomLeft: Number(values[3]) };
                    break;
            }
        }
        else {
            const evaluatedValue = Number(evaluateInputValue(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange));
            result = { topLeft: evaluatedValue, topRight: evaluatedValue, bottomRight: evaluatedValue, bottomLeft: evaluatedValue };
        }
        const overrides = getOverrideFields(['topLeft', 'topRight', 'bottomRight', 'bottomLeft'], options.overwritingRuleFieldValues);
        return { ...result, ...overrides };
    }
    static lengthVector(value, options) {
        const vector = evaluateVector3(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange);
        const overrides = getVectorOverrideFields(options.overwritingRuleFieldValues);
        return clampVectorFields({ ...vector, ...overrides }, options.minFieldValues, options.maxFieldValues);
    }
    static angleVector(value, options) {
        const vector = evaluateVector3(value, options.expressionEvaluationContext, options.angleUnit, "rad", options.anglePrecision, options.validRange);
        const overrides = getVectorOverrideFields(options.overwritingRuleFieldValues);
        return clampVectorFields({ ...vector, ...overrides }, options.minFieldValues, options.maxFieldValues);
    }
    static scalar(value, options) {
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, undefined, undefined, undefined, options.validRange);
        const clampValue = typeof evaluatedValue === 'number' ? evaluatedValue : options.default;
        // Note: NaN clampValue case covered by clampValueFields(..)
        return (typeof clampValue === 'number') ? clampValueFields(clampValue, options.minFieldValues, options.maxFieldValues) : undefined;
    }
    static scalarVector(value, options) {
        const vector = evaluateVector3(value, options.expressionEvaluationContext, undefined, undefined, undefined, options.validRange, 1);
        const overrides = getVectorOverrideFields(options.overwritingRuleFieldValues);
        return clampVectorFields({ ...vector, ...overrides }, options.minFieldValues, options.maxFieldValues);
    }
    static text(value, options) {
        const evaluatedValue = evaluateInputValue(value, options.expressionEvaluationContext, options.lengthUnit, undefined, options.lengthPrecision, options.validRange, true);
        return evaluatedValue === null || evaluatedValue === void 0 ? void 0 : evaluatedValue.toString();
    }
    static stringOption(value, _options) {
        return value;
    }
    static spaceSeparatedValues(value, _options) {
        return value.split(" ").map((item) => item === null || item === void 0 ? void 0 : item.trim());
    }
    static dynamicOption(value, _options) {
        return value;
    }
    static dynamicMultiOption(value, options) {
        const strings = value.trim().split(/\s*,\s*/);
        return strings.map(s => this.dynamicOption(s, options));
    }
    static boardStackup(value, options) {
        var _a;
        const hasLayout = Boolean((_a = options.expressionEvaluationContext) === null || _a === void 0 ? void 0 : _a.layout);
        return getStackupLayersFromValue(value, hasLayout, options.ruleData);
    }
    static selectorLengthsList(value, options) {
        if (value.trim().length === 0) {
            return undefined;
        }
        const evaluatedValue = evaluateSelectorLengthsList(value, options.expressionEvaluationContext, options.lengthUnit, "m", options.lengthPrecision, options.validRange);
        // We cannot perform the clamping here :(
        return evaluatedValue;
    }
    parseRules(ruleNames) {
        var _a, _b;
        /**
         * We declare it to be type of `any` here because this function is about
         * to construct the object using `ruleNames` as keys. In other words, as
         * long as `ruleNames` is passed correctly, the `parsedRules` object is
         * guaranteed to contain the expected fields.
         *
         * Plus, compiler wont allow us do const parsedRules: ParsedRulesFor<T> = {} here.
         *
         * TODO: Maybe we can handle this better?
         */
        const parsedRules = {};
        const parseOptions = {
            lengthUnit: this.lengthUnit,
            angleUnit: this.angleUnit,
            lengthPrecision: SharedResources.behaviors.pcb_editor.gridPositionInMetersDecimals,
            anglePrecision: SharedResources.behaviors.pcb_editor.gridPositionInRadDecimals,
            nodeType: this.nodeType,
            expressionEvaluationContext: this.expressionEvaluationContext,
        };
        for (const name of ruleNames) {
            const ruleConfig = LayoutRules[name];
            let options = { ...parseOptions };
            if (ruleConfig.overwritingRules) {
                const overwritingRules = this.parseRules(ruleConfig.overwritingRules);
                options.overwritingRuleFieldValues = ruleConfig.overwritingRules.map((key) => overwritingRules[key]);
            }
            if (ruleConfig.minRules) {
                const minFieldValues = this.parseRules(ruleConfig.minRules);
                options.minFieldValues = ruleConfig.minRules.map((key) => minFieldValues[key]);
            }
            if (ruleConfig.maxRules) {
                const maxFieldValues = this.parseRules(ruleConfig.maxRules);
                options.maxFieldValues = ruleConfig.maxRules.map((key) => maxFieldValues[key]);
            }
            options.validRange = ruleConfig.validRange;
            options.default = ruleConfig.default;
            const rule = this.rules[name];
            options.ruleData = rule;
            let value = rule === null || rule === void 0 ? void 0 : rule.value;
            if (isUndefined(value) || value === "") {
                let defaultValue = ruleConfig.default;
                // If there's no defaultValue, lets fallback to the original `value` instead of undefined,
                // and let `RuleParser.parseValue` below to decide what the parsedValue is
                value = defaultValue === undefined ? value : defaultValue.toString();
            }
            else {
                value = value.toString();
            }
            let parsedValue = undefined;
            // If the user enters a wrong unit this can throw an exception and crash
            try {
                parsedValue = value === undefined ? value : RuleParser.parseValue(ruleConfig.valueType, value, options);
            }
            catch (e) {
                // TODO: Do something better with this error, like showing it to the user
                // For now we just use a default or empty value
                parsedValue = RuleParser.parseValue(ruleConfig.valueType, (_a = ruleConfig.default) !== null && _a !== void 0 ? _a : "", options);
            }
            parsedRules[name] = parsedValue;
            // keep the context updated as rules get parsed to allow expressions to reference each other
            // Limitations: this only works if the dependencies got parsed before the dependent rule!
            if ((_b = this.expressionEvaluationContext) === null || _b === void 0 ? void 0 : _b.this) {
                if (!this.expressionEvaluationContext.this.bakedRules) {
                    this.expressionEvaluationContext.this.bakedRules = {};
                }
                this.expressionEvaluationContext.this.bakedRules[name] = parsedValue;
            }
        }
        return parsedRules;
    }
}
// Spaces are separators, except inside double quotes
function splitStringComponentsByWhitespace(value) {
    const trimmed = value.trim();
    // First step: split by space and double quotes
    const groupsRegex = /(\\\\"|[^\s\n"])+|(?:"){2,}|"(?!")((\\\\"|[^"])*)"|"/g;
    const matches = [];
    let match = null;
    while ((match = groupsRegex.exec(trimmed))) {
        if (match[2]) {
            // Double quoted group
            matches.push({ index: match.index, value: match[2] });
        }
        else {
            // No quote group present
            matches.push({ index: match.index, value: match[0] });
        }
    }
    // This will split cases like abc"abc" into two tokens, but we want them to be joined
    // I don't know how to regex, so I'm going to use a reduce
    return matches.reduce((prev, next, index) => {
        if (index === 0)
            return [next.value];
        const last = prev[prev.length - 1];
        // Space is the only separator, so if we splitted and there was not a space, then it has to be joined
        if (trimmed[next.index - 1] !== " " && trimmed[next.index - 1] !== "\n") {
            return [...prev.slice(0, -1), last + next.value];
        }
        else {
            return [...prev, next.value];
        }
    }, []);
}
export function evaluateVector3(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, zFallbackValue = 0) {
    const trimmedValue = value.trim();
    if (trimmedValue === "" || trimmedValue === SystemPcbLayoutRulesValues.auto) {
        return { x: 0, y: 0, z: 0, autoX: true, autoY: true, autoZ: true };
    }
    if (!isExpression(trimmedValue)) {
        const values = splitStringComponentsByWhitespace(trimmedValue).map((componentValue) => {
            const result = evaluateInputValue(componentValue, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, false);
            // If for example, the user specifies invalid expression for one or more vector components,
            // then result will be of string type. Preemptively catch, and resolve to expected number type,
            // in the best way we can:
            if (typeof result !== "number" || Number.isNaN(result)) {
                return validRange ? clampBetween(0, validRange[0], validRange[1]) : 0;
            }
            else {
                return result;
            }
        });
        return {
            x: values[0],
            y: values[values.length > 1 ? 1 : 0],
            z: values.length > 2 ? values[2] : values.length === 1 ? values[0] : zFallbackValue,
            autoX: false,
            autoY: false,
            autoZ: false,
        };
    }
    else {
        const evaluateValue = evaluateInputValue(trimmedValue, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, false);
        return {
            x: Number(evaluateValue),
            y: Number(evaluateValue),
            z: Number(evaluateValue) || zFallbackValue,
            autoX: false,
            autoY: false,
            autoZ: false,
        };
    }
}
function getVectorOverrideFields(overwritingRuleFieldValues) {
    if (!overwritingRuleFieldValues) {
        return {};
    }
    // Seems like the repetition below should be easily eliminated, but although
    // it can be, the end solution doesn't end up being an improvement, because
    // the type-friendly DRY solution is more complicated and no less code.
    const overrides = {};
    if (overwritingRuleFieldValues[0] === SystemPcbLayoutRulesValues.auto) {
        overrides["autoX"] = true;
    }
    else if (overwritingRuleFieldValues[0] !== undefined) {
        overrides["autoX"] = false;
        overrides.x = overwritingRuleFieldValues[0];
    }
    if (overwritingRuleFieldValues[1] === SystemPcbLayoutRulesValues.auto) {
        overrides["autoY"] = true;
    }
    else if (overwritingRuleFieldValues[1] !== undefined) {
        overrides["autoY"] = false;
        overrides.y = overwritingRuleFieldValues[1];
    }
    if (overwritingRuleFieldValues[2] === SystemPcbLayoutRulesValues.auto) {
        overrides["autoZ"] = true;
    }
    else if (overwritingRuleFieldValues[2] !== undefined) {
        overrides["autoZ"] = false;
        overrides.z = overwritingRuleFieldValues[2];
    }
    return overrides;
}
function clampVectorFields(vector = {}, minFieldValues, maxFieldValues) {
    if (minFieldValues) {
        minFieldValues.forEach((minFieldValue) => {
            if (minFieldValue === undefined) {
                return;
            }
            if (vector.x !== undefined) {
                vector.x = Math.max(minFieldValue.x, vector.x);
            }
            if (vector.y !== undefined) {
                vector.y = Math.max(minFieldValue.y, vector.y);
            }
            if (vector.z !== undefined) {
                vector.z = Math.max(minFieldValue.z, vector.z);
            }
        });
    }
    if (maxFieldValues) {
        maxFieldValues.forEach((maxFieldValue) => {
            if (maxFieldValue === undefined) {
                return;
            }
            if (vector.x !== undefined) {
                vector.x = Math.min(maxFieldValue.x, vector.x);
            }
            if (vector.y !== undefined) {
                vector.y = Math.min(maxFieldValue.y, vector.y);
            }
            if (vector.z !== undefined) {
                vector.z = Math.min(maxFieldValue.z, vector.z);
            }
        });
    }
    return vector;
}
function evaluateSelectorLengthsList(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange) {
    const tokens = splitStringComponentsByWhitespace(value).map((token) => token.trim());
    const parsedTokens = tokens.map((token) => {
        let selector = "*";
        let value = "";
        const hasSelector = token.includes("(");
        if (hasSelector) {
            const bracketIndex = token.indexOf("(");
            selector = token.substring(0, bracketIndex);
            value = token.substring(bracketIndex + 1, token.length - 1);
        }
        else {
            value = token;
        }
        return {
            selector,
            value: evaluateInputValue(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, false),
        };
    });
    return parsedTokens;
}
function clampValueFields(value, minFieldValues, maxFieldValues) {
    if (typeof value === "number") {
        let result = value;
        if (Number.isNaN(result)) {
            // NaN is a number type, which means we can arrive here with an unusable 'number'. To avoid propagating
            // unusable NaN's forward to the system, preemptively default to zero. If min/max ranges are applicable,
            // the subsequent logic will clamp result as required
            result = 0;
        }
        if (minFieldValues) {
            minFieldValues.forEach((minFieldValue) => {
                // Attempting Math.max() between NaN and valid number will result in NaN, so skipping over NaN is
                // important here
                if (minFieldValue === undefined || Number.isNaN(minFieldValue)) {
                    return;
                }
                result = Math.max(minFieldValue, result);
            });
        }
        if (maxFieldValues) {
            maxFieldValues.forEach((maxFieldValue) => {
                // Attempting Math.min() between NaN and valid number will result in NaN, so skipping over NaN is
                // important here
                if (maxFieldValue === undefined || Number.isNaN(maxFieldValue)) {
                    return;
                }
                result = Math.min(maxFieldValue, value);
            });
        }
        return result;
    }
    else {
        return value;
    }
}
function getOverrideFields(fields, values) {
    const overrides = {};
    for (var idx = 0; idx < fields.length; idx++) {
        if ((values === null || values === void 0 ? void 0 : values[idx]) !== undefined) {
            overrides[fields[idx]] = values[idx];
        }
    }
    return overrides;
}
function evaluateInputValue(value, expressionEvaluationContext, defaultInputUnit, convertToUnit, precision, validRange, returnString) {
    const inputValueDefault = 0;
    let expression = value;
    if (defaultInputUnit && isNumeric(value)) {
        expression = `${value} ${defaultInputUnit}`;
    }
    let result = inputValueDefault;
    if (expression !== "") {
        try {
            const evaluation = evaluateMechanicalValue(expression, true, expressionEvaluationContext);
            // If the expression is just a unit on it's own, like "mm", then the evaluation will succeed
            // and the following normaliziation will transform "mm" to a number `0.001`. A unit on it's own
            // would be considered an invalid expression, rather than evaluate to a decimal. This case
            // corresponds to `evaluation?.value === null` - throw an error to invoke default fallback
            // behavior
            if ((evaluation === null || evaluation === void 0 ? void 0 : evaluation.value) === null) {
                throw new Error();
            }
            result = normalizeEvaluatedExpression(evaluation, false, false, convertToUnit, defaultInputUnit);
        }
        catch (_a) {
            // It's possible for normalizeEvaluatedExpression(..) to throw an exception
            // in the case that value is a unit, eg value = "A"
            // Catching the error, allows fallback/default behavior to kick in from here
        }
    }
    if (result === undefined || result === null) {
        return inputValueDefault;
    }
    if (returnString === true) {
        return result.toString();
    }
    if (typeof result === "number") {
        if (Number.isNaN(result)) {
            // A NaN is a number type, so we could potentially arrive here with a NaN, which we'd then could attempt to clamp or format for
            // precision adjustment. Worse still, we could simply return a NaN which would then be forwarded to the system as an unusable
            // number. Doing this ensures that the return value is a value of number type that is directly usable in arithmetic
            result = inputValueDefault;
        }
        if (precision !== undefined) {
            result = Number(result.toFixed(precision));
        }
        if (validRange) {
            result = clampBetween(result, validRange[0], validRange[1]);
        }
        return result;
    }
    return inputValueDefault;
}
export function getStackupLayersFromValue(stackupValue, hasLayout, ruleData, parseCustomValues = true) {
    var _a;
    let stackupConfig;
    if (!Number.isNaN(Number(stackupValue))) {
        stackupConfig = Object.values(stackups.standard).find((stackup) => Object.values(stackup.layers).filter((layerConfig) => isCopperLayer(layerConfig)).length ===
            Number(stackupValue));
    }
    else {
        if (stackupValue &&
            stackupValue.toString().toLocaleLowerCase().includes("custom") &&
            (ruleData === null || ruleData === void 0 ? void 0 : ruleData.payloadAsBase64)) {
            try {
                const stackups = JSON.parse(atob(ruleData.payloadAsBase64));
                stackupConfig = stackups[stackupValue];
                if (parseCustomValues && stackupConfig) {
                    stackupConfig = parseCustomStackupValues(stackupConfig);
                }
            }
            catch (_b) {
                // Bad data, fall back to the default stackup
            }
        }
        else {
            // TODO: replace Object.values(..) with iteration over stackups, via for(..in..) loop, over raw object.
            // This is usually much faster than Object.values(..), about 2x as fast https://jsbench.me/v6lpuomea4/1
            Object.values(stackups).some((stackupCategory) => {
                stackupConfig = Object.values(stackupCategory).find((stackup) => stackup.key === stackupValue || stackup.label === stackupValue);
                return !!stackupConfig;
            });
        }
    }
    if (!stackupConfig) {
        const defaultKey = hasLayout ? defaultLayoutStackupKey : defaultFootprintStackupKey;
        stackupConfig = Object.values(stackups.standard).find((stackup) => stackup.key === defaultKey);
    }
    return (_a = stackupConfig === null || stackupConfig === void 0 ? void 0 : stackupConfig.layers) !== null && _a !== void 0 ? _a : {};
}
const lengthValues = ["thickness"];
function parseCustomStackupValues(stackupConfig) {
    // Convert any length values to numbers
    const layers = Object.entries(stackupConfig.layers).reduce((acc, [layerId, layerConfig]) => {
        acc[layerId] = {
            ...layerConfig,
            ...Object.entries(layerConfig).reduce((acc, [key, value]) => {
                if (lengthValues.includes(key)) {
                    acc[key] = getLengthValue(value);
                }
                return acc;
            }, {}),
        };
        return acc;
    }, {});
    return { ...stackupConfig, layers };
}
function getLengthValue(value) {
    if (typeof value === "number") {
        return value;
    }
    try {
        const length = evaluateMechanicalValue(value, true);
        return length.toNumber("m");
    }
    catch (_a) {
        // Fallback to a sane value on error
        return 0;
    }
}
//# sourceMappingURL=PcbLayoutRuleParser.js.map